"use client";

import React from "react";
import { Monitor, Film, ShieldCheck, ArrowRight, Layout } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { buttonVariants } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const services = [{
  title: "Show your program professionally",
  description: "Present your breeding philosophy, your dogs, your accomplishments, and your standards in a polished format",
  icon: Monitor
}, {
  title: "Build trust with puppy buyers",
  description: "Make health testing, process, and expectations easier to understand.\n\n",
  icon: Film
}, {
  title: "Get better quality inquiries\n\n",
  description: "Use structured forms to filter for the homes that best fit your dogs.\n\n\n",
  icon: ShieldCheck
}, {
  title: "Stay easy to update",
  description: "Your site is built to make new photos, litter pages, and dog updates simple to manage.\n\n",
  icon: Layout
}];
export default function TheConciergeSuite() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-concierge-suite" className="bg-background">
      <div className="bg-primary py-24 sm:py-32 relative overflow-hidden">
        {}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-primary-foreground)_0%,transparent_40%)] opacity-5 pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,var(--color-primary-foreground)_0%,transparent_40%)] opacity-5 pointer-events-none"></div>

        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
          <div ref={headerRef} className={`mx-auto max-w-2xl text-center transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-primary-foreground font-serif text-base/7 font-semibold tracking-widest uppercase">What Pupzstudio does</h2>
            <p className="mt-2 text-4xl font-serif tracking-tight text-background sm:text-5xl">Built Specifically for dog breeders.</p>
            <p className="mt-6 text-lg/8 text-background/80 font-sans tracking-wide">Generic website templates don’t reflect the way breeders actually work. Pupzstudio creates websites with breeder-specific page structures, including about your breed, about you, sire and dam pages, planned and current litter sections, puppy application forms, FAQ content, and health testing pages. The result is a website that feels more credible, answers common questions up front, and saves you time managing inquiries.</p>
          </div>

          <div ref={gridRef} className={`mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-4 transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            {services.map((service, i) => <div key={service.title} data-index={i} className="h-full">
                <Card className="h-full flex flex-col bg-white/5 backdrop-blur-md border border-primary-foreground/30 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-primary-foreground hover:shadow-[0_0_25px_var(--color-primary-foreground)] group">
                  <CardHeader className="pb-4">
                    <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-primary-foreground/10 border border-primary-foreground/20 group-hover:bg-primary-foreground/20 transition-colors duration-300">
                      <service.icon className="h-7 w-7 text-primary-foreground" aria-hidden="true" />
                    </div>
                    <CardTitle className="font-serif text-2xl text-background tracking-wide">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <CardDescription className="font-sans text-base text-background/70 leading-relaxed tracking-wide">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                  <CardFooter className="pt-4 pb-8">
                    <Link to="/OurServicesPage" className={buttonVariants({
                  variant: "outline",
                  className: "w-full border-primary-foreground/50 text-primary-foreground bg-transparent hover:bg-primary-foreground hover:text-primary transition-all duration-300 rounded-full font-sans tracking-wider uppercase text-xs py-6"
                })}>
                      Explore Service
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </CardFooter>
                </Card>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}